import { Modal } from '@byecode/ui'
import type { DataSourceAbstract, Field, FieldADTValue, ViewBlockAbstract } from '@lighthouse/core'
import { DataSourceType, SelectedMode } from '@lighthouse/core'
import type { ColumnEvent, CreateFieldParams, InsertDirection, RowEvent } from '@lighthouse/shared'
import {
    createFieldNeedFetchData,
    getMainTableRecordId,
    getQuickFilterRule,
    getWithScopeId,
    pageStackPubSub,
    SuspendPagination,
    Table,
    updateFieldNeedFetchData,
    useApplicationContext,
    useAtomAction,
    useAtomData,
    useLanguageContext
} from '@lighthouse/shared'
import { useAtomCallback } from 'jotai/utils'
import { find } from 'rambda'
import React, { useCallback, useId, useMemo, useRef } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { createViewRecordInDataDrawerAtom } from '@/atoms/blockRecordsDict/action'
import {
    createFieldAtom,
    deleteFieldAtom,
    deleteRecordAtom,
    deleteViewRecordAtom,
    updateCellAtom,
    updateFieldAtom
} from '@/atoms/dataSource/action'
import { recordPoolAtom, viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import { lastPageOfStackAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'
import { quickFilterCacheAtomFamily } from '@/atoms/storage/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useFilter } from '@/hooks/useFilter'
import { useLinkFilter } from '@/hooks/useLinkFilter'
import * as srv from '@/services'
import { getPexelsList } from '@/services'
import { uploadManagerInAppParams } from '@/utils/auth'

const SCxContainer = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 14px;
`

const SCxFooter = styled.div`
    position: absolute;
    z-index: 2;
    bottom: 15px;
    left: 50%;
    transform: translate(-50%, -50%);
`

interface ViewDataSourceProps {
    pageId: string
    blockData: ViewBlockAbstract
    dataSource: DataSourceAbstract
}

interface ViewDataSourceState {
    selectedRecords: string[]
    selectedMode?: SelectedMode
}

export const ViewDataSource: React.FC<ViewDataSourceProps> = ({ pageId, blockData, dataSource }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { id, schema, type, name } = dataSource
    const { id: blockId, config } = blockData
    const stackId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.stackId || '', [])
    )
    const scope = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s?.selectedNodes?.[0]?.scope, [])
    )

    const widthScopeId = getWithScopeId(blockId, scope)
    const { linkFilterController, filter: configFilter, pointer, quickFilter } = config

    const uploadId = useId()
    const isAggregateDataSource = type === DataSourceType.aggregateDataSource
    const { personOptions } = useApplicationContext()
    const dataSourceList = useDataSourceList(appId, envId)
    const viewIndependentData = useAtomData(viewIndependentDataAtomFamily({ stackId, dsId: id, viewId: widthScopeId }))
    const { recordPoints, search, currentPage, rowTotal } = viewIndependentData
    const { convertTextByLanguage } = useLanguageContext()

    const { run: createField } = useAtomAction(createFieldAtom)
    const { run: updateField } = useAtomAction(updateFieldAtom)
    const { run: deleteField } = useAtomAction(deleteFieldAtom)
    const { run: updateCell } = useAtomAction(updateCellAtom)
    const { run: createViewRecordInDataDrawer } = useAtomAction(createViewRecordInDataDrawerAtom)
    const { run: deleteRecord } = useAtomAction(deleteRecordAtom)
    const { run: deleteViewRecord } = useAtomAction(deleteViewRecordAtom)
    const selectedModeRef = useRef<SelectedMode>()
    // const { mutation, abortRef } = useHandleAbortPrevious(fetchDataSource)
    const createFieldParamsRef = useRef<CreateFieldParams>({
        sourceId: '',
        direction: '',
        fieldId: ''
    })
    const [state, setState] = useImmer<ViewDataSourceState>({
        selectedRecords: [],
        selectedMode: undefined
    })

    const { selectedRecords, selectedMode } = state

    useUpdateEffect(() => {
        if (selectedMode === 'ALL' && selectedModeRef.current === 'ALL') {
            setState(draft => {
                draft.selectedRecords = recordPoints?.map(p => p.id) || []
            })
        }
    }, [recordPoints])

    const getQuickFiltersRule = useAtomCallback(
        useCallback(
            get => {
                const dataSource = find(item => item.id === pointer, dataSourceList)
                if (!dataSource) {
                    return []
                }
                const quickFiltersCache = get(quickFilterCacheAtomFamily({ appId, envId, id }))
                return getQuickFilterRule({ quickFiltersCache, quickFilter, dataSource, personOptions })
            },
            [appId, dataSourceList, envId, id, personOptions, pointer, quickFilter]
        )
    )

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter: configFilter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    const handleSelectedRecords = useCallback(
        (selectedRecords: string[]) => {
            setState(draft => {
                draft.selectedRecords = selectedRecords
                draft.selectedMode = SelectedMode.CURRENT_PAGE
            })
            selectedModeRef.current = SelectedMode.CURRENT_PAGE
        },
        [setState]
    )

    const handleSelectModeChange = useCallback(
        (val?: SelectedMode) => {
            if (val === SelectedMode.ALL) {
                setState(draft => {
                    draft.selectedMode = SelectedMode.ALL
                    draft.selectedRecords = recordPoints.map(p => p.id)
                })
                selectedModeRef.current = SelectedMode.ALL
                return
            }
            if (val === SelectedMode.CURRENT_PAGE) {
                setState(draft => {
                    draft.selectedMode = SelectedMode.CURRENT_PAGE
                    draft.selectedRecords = recordPoints.map(p => p.id)
                })
                selectedModeRef.current = SelectedMode.CURRENT_PAGE
                return
            }
            setState(draft => {
                draft.selectedMode = undefined
                draft.selectedRecords = []
            })
            selectedModeRef.current = undefined
        },
        [recordPoints, setState]
    )

    const handleCellChange = useCallback(
        (recordId: string, fieldValue: FieldADTValue) =>
            updateCell({ envId, dsId: id, recordId, fieldId: fieldValue.id, value: fieldValue }),
        [envId, id, updateCell]
    )

    const handleCellUpdate = useCallback(
        (recordId: string, fieldValue: FieldADTValue) =>
            srv.updateCell({
                envId,
                dsId: id,
                id: recordId,
                fieldId: fieldValue.id,
                content: fieldValue
            }),
        [envId, id]
    )

    const handleCreateRecord = useCallback(
        (bId?: string) => {
            const beforeId = bId ? getMainTableRecordId(bId) : undefined
            return createViewRecordInDataDrawer({ stackId, viewId: blockId, appId, envId, dsId: id, beforeId })
        },
        [appId, blockId, createViewRecordInDataDrawer, envId, id, stackId]
    )

    const handleDeleteRecord = useCallback(
        async (recordIds: string[]) => {
            const isConfirm = await Modal.confirm({
                title: convertTextByLanguage('sureDelete'),
                content: convertTextByLanguage('deleteRecordWaring', {
                    count: selectedMode === 'ALL' ? rowTotal : recordIds.length
                }),
                cancelText: convertTextByLanguage('cancel'),
                okText: convertTextByLanguage('delete'),
                okStatus: 'error'
            })?.then(res => res)
            if (isConfirm) {
                const isDelete =
                    selectedMode !== 'ALL'
                        ? await deleteRecord({ envId, dsId: id, recordIds, mode: selectedMode })
                        : await deleteViewRecord({
                              envId,
                              viewId: blockId,
                              isSyncComponent: !!scope,
                              dsId: id,
                              recordIds,
                              mode: selectedMode,
                              search,
                              filter: resolvedFilter,
                              linkFilter: resolvedLinkFilter,
                              quickFilters: getQuickFiltersRule()
                          })
                if (isDelete) {
                    pageStackPubSub.emit(`${id}-UPDATE`)
                }
                return isDelete
            }
            return false
        },
        [
            convertTextByLanguage,
            selectedMode,
            rowTotal,
            deleteRecord,
            envId,
            id,
            deleteViewRecord,
            blockId,
            scope,
            search,
            resolvedFilter,
            resolvedLinkFilter,
            getQuickFiltersRule
        ]
    )

    const handleDeleteMultipleRecord = useCallback(async () => {
        const isDelete = await handleDeleteRecord(selectedRecords)
        if (isDelete) {
            setState(draft => {
                draft.selectedRecords = []
                draft.selectedMode = SelectedMode.CURRENT_PAGE
            })
            selectedModeRef.current = SelectedMode.CURRENT_PAGE
        }
    }, [handleDeleteRecord, selectedRecords, setState])

    const handleCreateField = useCallback(
        async (field: Field, sourceId: string, direction: InsertDirection) => {
            createFieldParamsRef.current = {
                sourceId,
                direction,
                fieldId: field.id
            }
            const innerType = await createField({
                envId,
                dsId: id,
                config: field,
                sourceId,
                direction
            })

            if (id !== field.dsId || createFieldNeedFetchData.has(field.type)) {
                pageStackPubSub.emit(`${id}-UPDATE`)
            }
            return !!innerType
        },
        [createField, envId, id]
    )

    const handleUpdateField = useCallback(
        async (fieldId: string, config: Field) => {
            const isUpdate = await updateField({
                envId,
                dsId: id,
                fieldId,
                config
            })
            const field = schema[fieldId]
            if ((field && field.type !== config.type) || updateFieldNeedFetchData.has(config.type)) {
                pageStackPubSub.emit(`${id}-UPDATE`)
            }
            return !!isUpdate
        },
        [envId, id, schema, updateField]
    )

    const handleDeleteField = useCallback(
        async (field: Field) => {
            const isConfirm = await Modal.confirm({
                title: '确认删除',
                content: `确认删除列「${field.name ?? '未命名列'}」？`,
                okStatus: 'error'
            })
            if (isConfirm) {
                const isDelete = await deleteField({
                    envId,
                    dsId: id,
                    fieldId: field.id
                })
                return !!isDelete
            }
            return false
        },
        [deleteField, envId, id]
    )

    const handleClose = useCallback(() => {
        setState(draft => {
            draft.selectedMode = undefined
            draft.selectedRecords = []
        })
    }, [setState])

    const uploadOptions = useMemo(
        () => ({
            info: { id: uploadId, label: name, groupId: id },
            options: uploadManagerInAppParams()
        }),
        [id, name, uploadId]
    )

    const videoUploadOptions = useMemo(() => {
        return {
            info: { id: uploadId, label: name, groupId: id },
            options: uploadManagerInAppParams()
        }
    }, [id, name, uploadId])

    useUpdateEffect(() => {
        handleClose()
    }, [currentPage])

    const rowEvent: RowEvent = useMemo(
        () => ({
            onRecordSelect: handleSelectedRecords,
            onCellChange: handleCellChange,
            onCellUpdate: handleCellUpdate,
            // onLoadMoreData: handleLoadDataSource,
            onCreateRecord: handleCreateRecord,
            onFetchPexelsList: getPexelsList
        }),
        [handleCellChange, handleCellUpdate, handleCreateRecord, handleSelectedRecords]
    )

    const columnEvent: ColumnEvent = useMemo(
        () => ({
            onCreateField: handleCreateField,
            onUpdateField: handleUpdateField,
            onDeleteField: handleDeleteField,
            onSelectModeChange: handleSelectModeChange
        }),
        [handleCreateField, handleUpdateField, handleDeleteField, handleSelectModeChange]
    )

    return (
        <>
            <SCxContainer>
                <Table
                    appId={appId}
                    headerFixed
                    disableFindUse
                    disableFieldCreatable={isAggregateDataSource}
                    disableSelect={isAggregateDataSource}
                    contentLoading={false}
                    data={dataSource}
                    viewIndependentData={viewIndependentData}
                    dataSourceList={dataSourceList}
                    createFieldParams={createFieldParamsRef.current}
                    recordPoolAtom={recordPoolAtom}
                    disableSelectMode
                    selectedRecords={selectedRecords}
                    // disableFieldCreatable
                    // disableAddRecord

                    pageSize={100}
                    columnEvent={columnEvent}
                    rowEvent={rowEvent}
                    uploadOptions={uploadOptions}
                    richTextUploadOptions={uploadManagerInAppParams()}
                    videoUploadOptions={videoUploadOptions}
                    scrollBarOutside={false}
                />
            </SCxContainer>
            <SCxFooter>
                {selectedRecords.length > 0 && (
                    <SuspendPagination
                        selectIds={selectedRecords}
                        total={recordPoints?.length}
                        mode={selectedMode}
                        enableDelete
                        onDelete={handleDeleteMultipleRecord}
                        onClose={handleClose}
                    />
                )}
            </SCxFooter>
        </>
    )
}
